/**
 * PDP Okendo reviews section. Same-page #reviews links (the buy-box
 * star row) smooth-scroll here, clearing the sticky header via
 * scroll-margin on the host. Respects prefers-reduced-motion.
 *
 * Widget Plus JS is supposed to come from the app embed. Theme-dev
 * previews often omit that embed, so load the CDN runtime once when
 * it is missing — filters / write-a-review need it. CSS for the
 * prerender is promoted in snippets/okendo-reviews-widget.liquid.
 *
 * The embed loads Widget Plus async in the head, so it can run before the
 * PDP widget is parsed. It then never boots that widget: the prerendered
 * markup shows but Show more / Filter / Sort do nothing. After load, boot any
 * widget it missed. Widget Plus stamps data-oke-rendered on the ones it has
 * booted, so nothing is initialised twice.
 */
(() => {
  const ANCHOR = 'reviews';
  const HASH = `#${ANCHOR}`;
  const WIDGET_PLUS_SRC = 'https://d3hw6dc1ow8pp2.cloudfront.net/reviews-widget-plus/js/okendo-reviews.js';

  // Grace after window load for the embed to boot Widget Plus by itself
  const EMBED_GRACE_MS = 1000;
  // How long to wait for okeWidgetApi once the runtime is on the page
  const API_POLL_MS = 250;
  const API_POLL_ATTEMPTS = 40;

  const loadWidgetPlus = () => {
    if (window.okeWidgetApi) return;
    if (document.querySelector('script[src*="reviews-widget-plus"]')) return;

    const host = document.querySelector('[data-oke-widget]');
    const shop = host && host.getAttribute('data-oke-shop');
    const script = document.createElement('script');

    script.src = shop ? `${WIDGET_PLUS_SRC}?shop=${encodeURIComponent(shop)}` : WIDGET_PLUS_SRC;
    script.async = true;
    document.head.appendChild(script);
  };

  const prefersReducedMotion = () => {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  };

  const isSamePageReviewsLink = (href) => {
    if (!href) return false;

    try {
      const url = new URL(href, window.location.href);

      return url.hash === HASH && url.pathname === window.location.pathname;
    } catch {
      return href === HASH;
    }
  };

  const scrollToReviews = () => {
    const section = document.getElementById(ANCHOR);

    if (!section) return;

    section.scrollIntoView({
      behavior: prefersReducedMotion() ? 'auto' : 'smooth',
      block: 'start',
    });
  };

  const handleClick = (event) => {
    if (event.defaultPrevented || event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    const link = event.target.closest('a[href]');

    if (!link || !isSamePageReviewsLink(link.getAttribute('href'))) return;

    event.preventDefault();
    scrollToReviews();

    if (window.location.hash !== HASH) {
      history.pushState(null, '', HASH);
    }
  };

  const initMissedWidgets = () => {
    const api = window.okeWidgetApi;

    if (!api || typeof api.initWidget !== 'function') return false;

    for (const widget of document.querySelectorAll('[data-oke-widget]:not([data-oke-rendered])')) {
      api.initWidget(widget);
    }

    return true;
  };

  const bootWidgets = () => {
    loadWidgetPlus();

    let attempts = 0;

    const poll = () => {
      if (initMissedWidgets()) return;

      attempts += 1;

      if (attempts < API_POLL_ATTEMPTS) {
        window.setTimeout(poll, API_POLL_MS);
      }
    };

    poll();
  };

  const bootWidgetsAfterLoad = () => {
    window.setTimeout(bootWidgets, EMBED_GRACE_MS);
  };

  document.addEventListener('click', handleClick);

  if (document.readyState === 'complete') {
    bootWidgetsAfterLoad();
  } else {
    window.addEventListener('load', bootWidgetsAfterLoad, { once: true });
  }
})();
